import { cn } from "@/lib/utils";

/**
 * The signature element. Three states, one is alive.
 *
 *  - `available`   — teal dot breathing inside an expanding ring. This is
 *    the only animated element in the product; it's reserved for "right
 *    now, this specialist is accepting referrals". It has to earn the motion.
 *  - `busy`        — solid warm dot (pb-warning). No animation.
 *  - `unavailable` — hollow muted ring. Communicates "listed but not active".
 *
 * Accessible label always present. Respects prefers-reduced-motion (animation
 * durations collapse to ~0ms via the base layer rule in index.css).
 */
export type Availability = "available" | "busy" | "unavailable";

const LABELS: Record<Availability, string> = {
  available: "Accepting referrals",
  busy: "Limited capacity",
  unavailable: "Not accepting referrals",
};

export function AvailabilityDot({
  state,
  size = "md",
  className,
  withLabel = false,
}: {
  state: Availability;
  size?: "sm" | "md" | "lg";
  className?: string;
  withLabel?: boolean;
}) {
  const dotSize = size === "sm" ? "h-2 w-2" : size === "lg" ? "h-3 w-3" : "h-2.5 w-2.5";
  const ringSize = size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-5 w-5" : "h-4 w-4";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap",
        className,
      )}
      aria-label={LABELS[state]}
      title={LABELS[state]}
    >
      <span className="relative inline-flex items-center justify-center" aria-hidden>
        {state === "available" && (
          <span
            className={cn(
              "absolute rounded-full bg-pb-primary/40 animate-pb-ring-expand",
              ringSize,
            )}
          />
        )}
        <span
          className={cn(
            "relative rounded-full",
            dotSize,
            state === "available" && "bg-pb-primary animate-pb-breathe",
            state === "busy" && "bg-pb-warning",
            state === "unavailable" && "border border-pb-ink-3/40 bg-transparent",
          )}
        />
      </span>
      {withLabel && (
        <span className="text-[11px] font-medium uppercase tracking-wider text-pb-ink-2">
          {LABELS[state]}
        </span>
      )}
    </span>
  );
}
